import { useSyncExternalStore } from "react";
import { ArrowClockwise } from "@phosphor-icons/react";
import { applyWaitingUpdate, getWaitingUpdate, subscribeToWaitingUpdate } from "./serviceWorker";

/**
 * The sentence that used not to exist.
 *
 * A deploy landed, a new service worker installed, it called `skipWaiting` and claimed the tab, and
 * the tab carried on running the bundle it had already parsed. Nothing said a new version existed
 * and nothing offered to load it, so the portal was out of date for as long as the tab stayed open
 * and the only way out was a reload nobody had a reason to perform.
 *
 * The worker now waits instead of taking over. This is the half that makes waiting useful: it
 * appears only when a worker is actually waiting, and its button is the only thing in the portal
 * that releases it.
 *
 * Mounted in `app/shell/AppShell.tsx`, at the top of the routed area, so it appears on every screen
 * a client can be on and never on the sign-in page, where a reload would throw away a half-typed
 * password. It shipped unmounted for one round, which is a worse state than the fault it replaces:
 * a worker that waits and a prompt nobody renders is a portal that never updates at all.
 *
*/
export function UpdatePrompt() {
  const waiting = useSyncExternalStore(subscribeToWaitingUpdate, getWaitingUpdate, () => null);

  if (!waiting) {
    return null;
  }

  /*
   * A toast above the bottom bar since 2026-09-24 (`prototype/kund/kund.js`, the `uppdatering`
   * overlay), so it does not push the page down under a person who is reading, and the one line it
   * carries is the warning that matters: save what you are writing first.
   */
  return (
    <section className="k-toast" role="status" data-testid="update-prompt">
      <ArrowClockwise size={18} aria-hidden="true" />
      <span>
        <strong>En ny version finns.</strong> Spara det du skriver först.
      </span>
      <button type="button" className="button is-primary" onClick={applyWaitingUpdate}>
        Uppdatera nu
      </button>
    </section>
  );
}
